import React, { Fragment } from 'react';

import { DateTime } from 'luxon';
import { Link } from 'react-router-dom';

import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { dateTimeFormatter, hockeyFormatter, statFormatter, stringFormatter } from '@nhl/formatter-utilities';
import PlayerLink from '@nhl/nhl-react-ui/lib/PlayerLink';

import { FranchisePages } from '../../../../../../redux/ducks/franchises/viewTypes';
import GlobalColumnFunctions from '../../../../../../utils/globalColumnFunctions';
import TeamDisplayModule from '../../../../../components/teamDisplayModule';

export const CreateHeaderCell = (statName, abbrev = statName, style = {}) => (
    <div title={statName} style={style}>
        {abbrev}
    </div>
);

const isHomeTeam = (score, triCode) => {
    return score.split('@')[0].slice(0, 3) !== triCode;
};

const opponentTriCode = (score, triCode) => {
    return score.split('@')[0].slice(0, 3) !== triCode
        ? score.split('@')[0].slice(0, 3)
        : score.split('@')[1].slice(1, 4);
};

export const sumValues = (data) => data.reduce((acc, item = 0) => acc + item, 0);

// Individual columns
// NOTE: Although faceoffWinPctg is an existing column in the statsFormatter Neil requested this column to show 2 decimal places
const FACEOFF_WIN_PCTG_COL = {
    accessor: 'faceoffWinPctg',
    Header: CreateHeaderCell('Faceoff Win Percentage', 'FOW%'),
    Cell: ({ value }) => statFormatter.percentageFormatter(2)(value, true, statFormatter.displayNAOrNullValue(value)),
    getProps: GlobalColumnFunctions.getProps,
    sortDescFirst: true,
    sortType: 'basic',
};

const getBoxScoreContent = (original: Record<string, any>, overtime = '') => {
    let opponentInfo = '';
    if (original.opponentTeamAbbrev || original.opponentTriCode) {
        opponentInfo = original.opponentTeamAbbrev || original.opponentTriCode;
    } else {
        opponentInfo = original.score ? opponentTriCode(original.score, original.triCode || original.teamAbbrev) : '';
    }
    return (
        <>
            {original.gameDescription ? `${original.gameDescription} ` : ''}
            {DateTime.fromISO(original.gameDate).toFormat('MM/dd/y')}
            {original.homeRoad
                ? original.homeRoad === 'R'
                    ? ' at '
                    : ' vs. '
                : original.score
                ? isHomeTeam(original.score, original.triCode || original.teamAbbrev)
                    ? ' vs. '
                    : ' at '
                : ' '}
            {opponentInfo}
            {overtime && ` ${overtime}`}
        </>
    );
};

const GAME_COL = {
    Header: CreateHeaderCell('Game'),
    accessor: 'gameDate',
    Cell: ({ original }) => (
        <a href={`https://www.nhl.com/gamecenter/${original.gameId}`} target="_blank">
            {getBoxScoreContent(original)}
        </a>
    ),
    minWidth: window.innerWidth > 414 ? 150 : 130,
    getProps: GlobalColumnFunctions.getProps,
};

const GAME_DATE_COL = {
    Header: CreateHeaderCell('Date'),
    accessor: 'gameDate',
    Cell: ({ original }) => `${DateTime.fromISO(original.gameDate).toFormat('MM/dd/y')}`,
    getProps: GlobalColumnFunctions.getProps,
    minWidth: 80,
};

const addVideoToGameDateColumn =
    (setVideoId) =>
    ({ original }) => {
        const videoId = original.recordToFranchise ? original.recordToFranchise[0].videoId : null;
        const gameDate = `${DateTime.fromISO(original.gameDate).toFormat('MM/dd/y')}`;
        return (
            <div className="videoSection">
                {gameDate}
                {videoId && (
                    <FontAwesomeIcon
                        icon={solid('film')}
                        className="videoIcon"
                        onClick={() => {
                            setVideoId(videoId);
                        }}
                    />
                )}
            </div>
        );
    };

const GAMES_PLAYED_COL = {
    accessor: 'gamesPlayed',
    Header: CreateHeaderCell('Games Played', 'GP'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    aggregate: sumValues,
};

const GAMES_STARTED_COL = {
    accessor: 'gamesStarted',
    Header: CreateHeaderCell('Games Started', 'GS'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};

const HITS_COL = {
    accessor: 'hits',
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    Header: CreateHeaderCell('Hits'),
    sortDescFirst: true,
};

const BLOCKS_COL = {
    accessor: 'blocks',
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    Header: CreateHeaderCell('Blocks'),
    sortDescFirst: true,
};

const HOME_GAMES_PLAYED_COL = {
    accessor: 'homeGamesPlayed',
    Header: CreateHeaderCell('Home Games Played', 'HGP'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};

const ROAD_GAMES_PLAYED_COL = {
    accessor: 'roadGamesPlayed',
    Header: CreateHeaderCell('Road Games Played', 'RGP'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};

// The INDEX_COL         will show ranks based on how the data is initially sorted in the table
// The CURRENT_INDEX_COL will show ranks based on how the data is currently sorted in the table
export const INDEX_COL = {
    Header: CreateHeaderCell('Rank'),
    id: 'index',
    accessor: 'index',
    Cell: ({ row }) => row.index + 1,
    minWidth: 45,
};

const CURRENT_INDEX_COL = {
    Header: CreateHeaderCell('Rank'),
    id: 'index',
    accessor: 'index',
    Cell: (original) => original.viewIndex + 1 + original.state.pageIndex * original.state.pageSize,
    minWidth: 45,
};

const addVideoToIndexColumn = (setVideoId, accessor) => (row) => {
    const { videoId } = row.original;
    const value = row[accessor] !== undefined ? row[accessor] : row.row[accessor];
    return (
        <div className="videoSection">
            <div>{value + 1}</div>
            {videoId && (
                <FontAwesomeIcon
                    icon={solid('film')}
                    className="videoIcon"
                    onClick={() => {
                        setVideoId(videoId);
                    }}
                />
            )}
        </div>
    );
};

const PERIOD_COL = {
    accessor: 'period',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Period'),
};

const GOAL_ONE_COL = (goalPeriodKey = 'goal1Period') => ({
    id: 'goalOne',
    Cell: ({ original }) => {
        const goalTimeOfPeriod = original.goal1TimeOfPeriod;
        const goalPeriod = original[goalPeriodKey];
        return `${goalTimeOfPeriod} (${goalPeriod})`;
    },
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Goal 1', 'G1'),
});

const GOAL_TWO_COL = (goalPeriodKey = 'goal2Period') => ({
    id: 'goalTwo',
    Cell: ({ original }) => {
        const goalTimeOfPeriod = original.goal2TimeOfPeriod;
        const goalPeriod = original[goalPeriodKey];
        return `${goalTimeOfPeriod} (${goalPeriod})`;
    },
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Goal 2', 'G2'),
});

const GOAL_THREE_COL = (goalPeriodKey = 'goal3Period') => ({
    id: 'goalThree',
    Cell: ({ original }) => {
        const goalTimeOfPeriod = original.goal3TimeOfPeriod;
        const goalPeriod = original[goalPeriodKey];
        return `${goalTimeOfPeriod} (${goalPeriod})`;
    },
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Goal 3', 'G3'),
});

const ASSIST_ONE_COL = {
    id: 'assistOne',
    Cell: ({ original }) => `${original.assists1TimeOfPeriod} (${original.assists1Period})`,
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Assist 1', 'A1'),
};

const ASSIST_TWO_COL = {
    id: 'assistTwo',
    Cell: ({ original }) => `${original.assists2TimeOfPeriod} (${original.assists2Period})`,
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Assist 2', 'A2'),
};

const ASSIST_THREE_COL = {
    id: 'assistThree',
    Cell: ({ original }) => `${original.assists3TimeOfPeriod} (${original.assists3Period})`,
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Assist 3', 'A3'),
};

const SINCE_START_OF_PERIOD_COL = {
    accessor: 'secondsSinceStartOfPeriod',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Seconds Since Start of Period', 'Seconds'),
};

export const createPlayerCol = ({ activePlayerKey = 'activePlayer' }) => ({
    Header: CreateHeaderCell('Player Name', 'Player', { textAlign: 'left' }),
    headerClassName: 'name left',
    className: 'name left',
    accessor: 'lastName',
    Cell: ({ original }) => (
        <Fragment>
            <PlayerLink
                player={original}
                title={original[activePlayerKey] ? 'Active Player' : null}
                className={original[activePlayerKey] ? 'strong' : ''}
            />
            {original.activeStreak && <span title="Streak is Active" className="active-indicator-icon" />}
        </Fragment>
    ),
    sortType: (a, b) =>
        a.original.lastName.toUpperCase() === b.original.lastName.toUpperCase()
            ? a.original.firstName.toUpperCase().localeCompare(b.original.firstName.toUpperCase())
            : a.original.lastName.toUpperCase().localeCompare(b.original.lastName.toUpperCase()),
    minWidth: window.innerWidth > 414 ? 150 : 130,
    getProps: GlobalColumnFunctions.getProps,
});

const PLAYER_COL = createPlayerCol({
    activePlayerKey: 'activePlayer',
});

const POSITION_COL = {
    Header: CreateHeaderCell('Position', 'Pos'),
    accessor: 'positionCode',
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    getProps: GlobalColumnFunctions.getProps,
};

const ROUND_NUMBER_COL = {
    accessor: 'roundNumber',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Round Number', 'Round'),
};

const SCORE_COL = {
    accessor: 'score',
    Header: CreateHeaderCell('Score'),
    getProps: GlobalColumnFunctions.getProps,
    minWidth: 120,
    Cell: ({ original }) => (
        <a href={`https://www.nhl.com/gamecenter/${original.gameId}`} target="_blank">
            {original.score}
        </a>
    ),
};

const SCORING_POINTS_COL = {
    accessor: 'scoringPoints',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Scoring Points', 'Points'),
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};

const GAME_SCORE_COL = {
    Header: 'Score',
    accessor: 'gameId',
    Cell: ({ original }) => (
        <a href={`https://www.nhl.com/gamecenter/${original.gameId}`} target="_blank">
            {original.homeRoad === 'R'
                ? original.teamAbbrev + ': ' + original.goalsFor
                : original.opponentAbbrev + ': ' + original.goalsAgainst}
            {' @ '}
            {original.homeRoad === 'R'
                ? original.opponentAbbrev + ': ' + original.goalsAgainst
                : original.teamAbbrev + ': ' + original.goalsFor}
        </a>
    ),
};

const SEASON_COL = {
    Header: CreateHeaderCell('Season'),
    accessor: 'seasonId',
    Cell: ({ original }) => {
        return original.seasonId !== undefined ? hockeyFormatter.singleSeasonFormatter(original.seasonId) : '';
    },
    getProps: GlobalColumnFunctions.getProps,
    minWidth: 70,
};

const FIRST_OUTDOOR_SEASON_COL = {
    Header: CreateHeaderCell('First Season'),
    accessor: 'firstSeasonForOutdoorGame',
    Cell: ({ original }) => {
        return original.firstSeasonForOutdoorGame
            ? hockeyFormatter.singleSeasonFormatter(original.firstSeasonForOutdoorGame)
            : '';
    },
    getProps: GlobalColumnFunctions.getProps,
    minWidth: 60,
    width: 100,
};

const LAST_OUTDOOR_SEASON_COL = {
    Header: CreateHeaderCell('Last Season'),
    accessor: 'lastSeasonForOutdoorGame',
    Cell: ({ original }) => {
        return original.lastSeasonForOutdoorGame
            ? hockeyFormatter.singleSeasonFormatter(original.lastSeasonForOutdoorGame)
            : '';
    },
    getProps: GlobalColumnFunctions.getProps,
    minWidth: 60,
    width: 100,
};

const TEAM_ABBREVS = {
    accessor: 'teamAbbrevs',
    Header: CreateHeaderCell('Teams Played For', 'Team(s)'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => (value ? value.replace(/,/g, ', ') : ''),
};

const TEAM_COL = {
    Header: CreateHeaderCell('Team', 'Team', { textAlign: 'left' }),
    accessor: 'teamName',
    Cell: ({ original, value }) => (
        <Fragment>
            {value ? value.replace(/,/g, ', ') : ''}
            {
                // need to check activePlayer === undefined so active flag is not present on skater records
                original.activeStreak && original.activePlayer === undefined && (
                    <span title="Streak is Active" className="active-indicator-icon" />
                )
            }
        </Fragment>
    ),
    sortType: (a, b) => a.original.teamName.toUpperCase().localeCompare(b.original.teamName.toUpperCase()),
    minWidth: window.innerWidth > 414 ? 150 : 130,
    getProps: GlobalColumnFunctions.getProps,
    headerClassName: 'left',
    className: 'left',
};

const TEAM_DISPLAY_COL = {
    Header: CreateHeaderCell('Team', 'Team', { textAlign: 'left' }),
    accessor: 'currentTeamName',
    Cell: ({ original }) => (
        <Fragment>
            <Link to={`/overview/${FranchisePages.SEASON_BY_SEASON_RECORD}`}>
                <TeamDisplayModule team={original.team} useInTable />
            </Link>
        </Fragment>
    ),
    minWidth: window.innerWidth > 414 ? 150 : 130,
    getProps: GlobalColumnFunctions.getProps,
    headerClassName: 'left',
    className: 'left',
};

const TEAMS_COL = {
    Header: CreateHeaderCell('Team', 'Team', { textAlign: 'left' }),
    Cell: ({ original }) => {
        if (!original.teamNames) {
            return;
        }
        const teamNamesArray = original.teamNames.split(',');
        return teamNamesArray.map((name, index) => {
            if (index === teamNamesArray.length - 1) {
                return name;
            }
            return <div key={name}>{name},</div>;
        });
    },
    accessor: 'teamNames',
    minWidth: window.innerWidth > 414 ? 150 : 130,
    getProps: GlobalColumnFunctions.getProps,
    style: { justifyContent: 'left', textAlign: 'left', flexFlow: 'wrap', whiteSpace: 'pre-line' },
};

const GOALS_COL = {
    accessor: 'goals',
    Header: CreateHeaderCell('Goals', 'G'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    sortDescFirst: true,
};

const GOALS_FOR_COL = {
    accessor: 'goals',
    Header: CreateHeaderCell('Goals For', 'GF'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    sortDescFirst: true,
    aggregate: sumValues,
};

const GOALS_AGAINST_COL = {
    accessor: 'goalsAgainst',
    Header: CreateHeaderCell('Goals Against', 'GA'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    sortDescFirst: true,
    aggregate: sumValues,
};

const POWER_PLAY_GOALS_COL = {
    accessor: 'powerPlayGoals',
    Header: CreateHeaderCell('Power Play Goals', 'PPG'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};

const POWER_PLAY_ASSISTS_COL = {
    accessor: 'powerPlayAssists',
    Header: CreateHeaderCell('Power Play Assists', 'PPA'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};

const POWER_PLAY_POINTS_COL = {
    accessor: 'powerPlayPoints',
    Header: CreateHeaderCell('Power Play Points', 'PPP'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};

const SHORTHAND_GOALS_COL = {
    accessor: 'shorthandedGoals',
    Header: CreateHeaderCell('Shorthanded Goals', 'SHG'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};

const SHORTHAND_ASSISTS_COL = {
    accessor: 'shorthandedAssists',
    Header: CreateHeaderCell('Shorthanded Assists', 'SHA'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};

const SHORTHAND_POINTS_COL = {
    accessor: 'shorthandedPoints',
    Header: CreateHeaderCell('Shorthanded Points', 'SHP'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};

const MULTI_GOAL_GAMES_COL = {
    accessor: 'multiGoalGames',
    Header: CreateHeaderCell('Multi-Goal Games'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    sortDescFirst: true,
};

const MULTI_ASSIST_GAMES_COL = {
    accessor: 'multiAssistGames',
    Header: CreateHeaderCell('Multi-Assist Games'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    sortDescFirst: true,
};

const MULTI_POINT_GAMES_COL = {
    accessor: 'multiPointGames',
    Header: CreateHeaderCell('Multi-Point Games'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    sortDescFirst: true,
};

const ASSISTS_COL = {
    accessor: 'assists',
    Header: CreateHeaderCell('Assists', 'A'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    sortDescFirst: true,
};

const BLOCKED_SHOTS_COL = {
    accessor: 'blockedShots',
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    Header: CreateHeaderCell('Blocked Shots', 'BkS'),
    sortDescFirst: true,
};

const POINTS_COL = {
    accessor: 'points',
    aggregate: sumValues,
    Header: CreateHeaderCell('Points', 'P'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    sortDescFirst: true,
};

const START_DATE_COL = {
    accessor: 'startDate',
    Header: CreateHeaderCell('Start Date'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => DateTime.fromISO(value).toFormat('MM/dd/y'),
};

const END_DATE_COL = {
    accessor: 'endDate',
    Header: CreateHeaderCell('End Date'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => DateTime.fromISO(value).toFormat('MM/dd/y'),
};

const START_SEASON_COL = {
    accessor: 'startSeason',
    Header: CreateHeaderCell('Start Season'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => (value ? hockeyFormatter.singleSeasonFormatter(value) : ''),
};

const END_SEASON_COL = {
    accessor: 'endSeason',
    Header: CreateHeaderCell('End Season'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => (value ? hockeyFormatter.singleSeasonFormatter(value) : ''),
};

const PENALTIES_COL = {
    accessor: 'penalties',
    Header: CreateHeaderCell('Penalties', 'Pen'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    sortDescFirst: true,
};

const PENALTY_MINUTES_COL = {
    accessor: 'penaltyMinutes',
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    Header: CreateHeaderCell('Penalty Minutes', 'PIM'),
    sortDescFirst: true,
};

const TIME_ON_ICE_COL = {
    accessor: 'timeOnIce',
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => dateTimeFormatter.displaySecondsFormatted(value),
    Header: CreateHeaderCell('Time On Ice', 'TOI'),
    sortDescFirst: true,
};

const TIME_ON_ICE_PER_GAME_COL = {
    id: 'timeOnIcePerGp',
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ original }) => dateTimeFormatter.displaySecondsFormatted(original.timeOnIce / original.gamesPlayed),
    Header: CreateHeaderCell('Time On Ice / Games Played', 'TOI/GP'),
    sortDescFirst: true,
};

const MISSED_SHOTS_COL = {
    accessor: 'missedShots',
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    Header: CreateHeaderCell('Missed Shots', 'MsS'),
    sortDescFirst: true,
};

const SHOTS_COL = {
    accessor: 'shots',
    Header: CreateHeaderCell('Shots', 'S'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};

const GIVEAWAYS_COL = {
    accessor: 'giveaways',
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    Header: CreateHeaderCell('Giveaways', 'GvA'),
    sortDescFirst: true,
};

const TAKEAWAYS_COL = {
    accessor: 'takeaways',
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    Header: CreateHeaderCell('Takeaways', 'TkA'),
    sortDescFirst: true,
};

const FACEOFFS_TAKEN_COL = {
    accessor: 'faceoffsTaken',
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    Header: CreateHeaderCell('Faceoffs Taken', 'FO Taken'),
    sortDescFirst: true,
};

const FACEOFFS_WON_COL = {
    accessor: 'faceoffsWon',
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    Header: CreateHeaderCell('Faceoffs Won', 'FOW'),
    sortDescFirst: true,
};

const FACEOFFS_LOST_COL = {
    accessor: 'faceoffsLost',
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    Header: CreateHeaderCell('Faceoffs Lost', 'FOL'),
    sortDescFirst: true,
};

const PLAYOFF_SEASONS_COL = {
    accessor: 'playoffSeasons',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Playoff Seasons', 'PS'),
    sortDescFirst: true,
};

const TIES_COL = {
    accessor: 'ties',
    aggregate: sumValues,
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    Header: CreateHeaderCell('Ties', 'T'),
    sortDescFirst: true,
};

const SHUTOUTS_COL = {
    accessor: 'shutouts',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Shutouts', 'SO'),
    sortDescFirst: true,
};

const SHOTS_AGAINST_COL = {
    accessor: 'shotsAgainst',
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    Header: CreateHeaderCell('Shots Against', 'SA'),
    sortDescFirst: true,
};

const SEASONS_PLAYED_COL = {
    accessor: 'seasonsPlayed',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Seasons Played', 'Seasons'),
    sortDescFirst: true,
};

const WIN_STREAK_COL = {
    accessor: 'winStreak',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Win Streak'),
    sortDescFirst: true,
};

const SERIES_TITLE = {
    accessor: 'seriesTitle',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Series Title', 'Series'),
};

const EVENT_NAME_COL = {
    accessor: 'eventName',
    Header: 'Event',
    getProps: GlobalColumnFunctions.getProps,
    minWidth: window.innerWidth > 414 ? 160 : 130,
};

const WINS_COL = {
    accessor: 'wins',
    aggregate: sumValues,
    Header: CreateHeaderCell('Wins', 'W'),
    getProps: GlobalColumnFunctions.getProps,
    sortDescFirst: true,
};

const LOSSES_COL = {
    accessor: 'losses',
    aggregate: sumValues,
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Losses', 'L'),
    sortDescFirst: true,
};
const OVERTIME_GAMES_PLAYED_COL = {
    accessor: 'overtimeGamesPlayed',
    Header: CreateHeaderCell('Overtime Games Played', 'OT GP'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};
const OVERTIME_WINS_COL = {
    accessor: 'overtimeWins',
    Header: CreateHeaderCell('Overtime Wins', 'OT W'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};
const OVERTIME_LOSSES_COL = {
    accessor: 'overtimeLosses',
    aggregate: sumValues,
    Header: CreateHeaderCell('Overtime Losses', 'OT'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};
const OVERTIME_TIES_COL = {
    accessor: 'overtimeTies',
    Header: CreateHeaderCell('Overtime Ties', 'OT T'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};
const OVERTIME_WIN_PERCENTAGE_COL = {
    accessor: 'otWinPctg',
    Header: CreateHeaderCell('Overtime Win Percentage', 'OT W%'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ row }) => {
        const games = row.values.overtimeGamesPlayed;
        const wins = row.values.overtimeWins;
        const ties = row.values.overtimeTies || 0;
        return statFormatter.displayValueAs('pointPctg')((wins * 2 + ties) / (games * 2));
    },
    sortDescFirst: true,
    sortType: (a, b) => {
        const getWinPctg = (data) => (data.overtimeWins * 2 + data.overtimeTies) / (data.overtimeGamesPlayed * 2);
        const value1 = getWinPctg(a.original);
        const value2 = getWinPctg(b.original);
        return value1 > value2 ? 1 : -1;
    },
};
const OT_PERIOD_COL = {
    accessor: 'period',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('OT Period'),
    Cell: ({ original }) => original.period - 3,
};

const CUPS_WON_COL = {
    Header: CreateHeaderCell('Cups Won', 'Cups'),
    accessor: 'cupsWon',
    getProps: GlobalColumnFunctions.getProps,
    sortDescFirst: true,
};
const LONGEST_STREAK_COL = {
    Header: CreateHeaderCell('Longest Streak'),
    accessor: 'longestStreak',
    getProps: GlobalColumnFunctions.getProps,
    sortDescFirst: true,
};
const STREAK_DESC_COL = {
    Header: CreateHeaderCell('Longest Streak Description'),
    accessor: 'longestStreakDescription',
    className: 'streaks',
    Cell: ({ original }) => {
        if (original.longestStreak > 1) {
            const streakArray = original.longestStreakDescription.split(', ');
            const sortedStreaks = [...streakArray].sort((a, b) => a.slice(0, 4) - b.slice(0, 4));
            return sortedStreaks.map((streak, idx) => <div key={idx}>{streak}</div>);
        } else {
            return original.longestStreak === 1 || original.longestStreakDescription === null
                ? '--'
                : original.longestStreakDescription;
        }
    },
    sortType: (a, b) => {
        const value1 = a.original.longestStreakDescription.slice(0, 4);
        const value2 = b.original.longestStreakDescription.slice(0, 4);
        return value1 < value2 ? -1 : 1;
    },
    getProps: GlobalColumnFunctions.getProps,
};
const SEASONS_WON_COL = {
    Header: CreateHeaderCell('Seasons Won'),
    accessor: 'seasonsWon',
    Cell: ({ value }) => {
        const seasons = value && value.split(', ');
        return seasons && seasons.map((season, idx) => <div key={idx}>{season}</div>);
    },
    className: 'seasons',
    getProps: GlobalColumnFunctions.getProps,
};

const SERIES_GAME_NUMBER_COL = {
    Header: CreateHeaderCell('Series Game #'),
    accessor: 'seriesGameNumber',
    getProps: GlobalColumnFunctions.getProps,
};

const GAME_NUMBER_COL = {
    ...SERIES_GAME_NUMBER_COL,
    Header: CreateHeaderCell('Game #'),
};

const OPPONENT_COL = {
    accessor: 'opponentTeamName',
    getProps: GlobalColumnFunctions.getProps,
    minWidth: window.innerWidth > 414 ? 150 : 130,
    headerClassName: 'left',
    className: 'left',
    Header: CreateHeaderCell('Opponent Team Name', 'Opponent', { textAlign: 'left' }),
};

const APPEARANCES_COL = {
    accessor: 'appearances',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Appearances'),
};

const STANLEY_CUPS_COL = {
    accessor: 'stanleyCups',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Stanley Cups'),
};

const CONSEC_SEASONS_COL = {
    accessor: 'consecutiveSeasons',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Consecutive Appearances'),
};

const STREAK_SEASONS_COL = {
    accessor: 'startSeason',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Streak Seasons'),
    Cell: ({ row }) => {
        return (
            hockeyFormatter.singleSeasonFormatter(row.original.startSeason) +
            ' - ' +
            hockeyFormatter.singleSeasonFormatter(row.original.endSeason)
        );
    },
};

const WIN_PCTG_COL = {
    id: 'winPctg',
    Header: CreateHeaderCell('Win Percentage', 'W%'),
    Cell: (row) =>
        statFormatter.displayValueAs('winPctg')(row.original.wins / (row.original.games || row.original.gamesPlayed)),
    getProps: GlobalColumnFunctions.getProps,
};

const AGE_COLUMN = {
    accessor: 'ageInDays',
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ original }) => original.yearsDays,
    Header: CreateHeaderCell('Age: Years/Days', 'Age'),
    sortType: (a, b) => {
        if (a.original.ageInDays === b.original.ageInDays) {
            return a.original.ageInDaysForYear - b.original.ageInDaysForYear;
        }
        return a.original.ageInDays - b.original.ageInDays;
    },
};

const LATE_GOAL_SCORE_COL = {
    accessor: 'homeScore',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Score'),
    Cell: ({ row }) => (
        <a href={`https://www.nhl.com/gamecenter/${row.original.gameId}`} target="_blank">
            {row.original.homeRoad === 'R'
                ? row.original.team.triCode +
                  ' ' +
                  row.original.visitorScore +
                  ' @ ' +
                  row.original.opponent.triCode +
                  ' ' +
                  row.original.homeScore
                : row.original.opponent.triCode +
                  ' ' +
                  row.original.visitorScore +
                  ' @ ' +
                  row.original.team.triCode +
                  ' ' +
                  row.original.homeScore}
        </a>
    ),
};

const SECONDS_REMAINING_COL = {
    accessor: 'secondsOfGame',
    Header: CreateHeaderCell('Seconds Remaining', 'Time'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => 3600 - value,
};

const GAME_WINNING_GOAL_COL = {
    accessor: 'gameWinningGoal',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('GWG'),
    Cell: ({ value }) => (value === true ? 'Y' : 'N'),
};

const AMATEUR_LEAGUE_COL = {
    Header: 'Amateur League',
    headerClassName: 'amateur-league-header',
    className: 'amateur-league',
    accessor: 'amateurLeague',
    minWidth: 120,
    getProps: GlobalColumnFunctions.getProps,
};

const AMATEUR_TEAM_COL = {
    Header: 'Amateur Team',
    headerClassName: 'amateur-team-header',
    className: 'amateur-team',
    accessor: 'amateurClubName',
    minWidth: 170,
    getProps: GlobalColumnFunctions.getProps,
};

// Common groupings of columns -- not including index because we want to add colouring for all columns except index
const GAP_COLUMNS = [GOALS_COL, ASSISTS_COL, POINTS_COL];
const PLAYER_BASE_COLS = [INDEX_COL, PLAYER_COL, POSITION_COL];
const PLAYER_TEAM_BASE_COLS = [INDEX_COL, PLAYER_COL, TEAM_COL];
const PLAYER_TEAMS_BASE_COLS = [INDEX_COL, PLAYER_COL, TEAM_ABBREVS];
const MULTI_GAP_GAMES_COLUMNS = [MULTI_GOAL_GAMES_COL, MULTI_ASSIST_GAMES_COL, MULTI_POINT_GAMES_COL];
const LONGEST_TIME_COLUMNS = [START_DATE_COL, END_DATE_COL];
const START_END_SEASON_COLS = [START_SEASON_COL, END_SEASON_COL];
const PLAYER_ALL_TIME = [INDEX_COL, PLAYER_COL, TEAM_ABBREVS, GAMES_PLAYED_COL];
const PLAYER_ALL_TIME_PLAYOFFS = [INDEX_COL, PLAYER_COL, POSITION_COL, TEAM_ABBREVS];
const PLAYER_MULTI_ALL_TIME = [INDEX_COL, PLAYER_COL, POSITION_COL, TEAM_ABBREVS, GAMES_PLAYED_COL];
const PLAYER_ALL_TIME_WITHOUT_GP_COLS = [INDEX_COL, PLAYER_COL, TEAM_ABBREVS];
const PLAYER_GAME_COLS = [INDEX_COL, PLAYER_COL, TEAM_COL, POSITION_COL, GAME_COL];
const GOALIE_GAME_COLS = [INDEX_COL, PLAYER_COL, TEAM_COL, GAME_COL];
const PLAYER_STANLEY_CUPS_COLS = [CUPS_WON_COL, LONGEST_STREAK_COL, STREAK_DESC_COL, SEASONS_WON_COL];
const YOUNGEST_BASE_COLS = [INDEX_COL, PLAYER_COL, TEAM_COL];
const PLAYER_SEASON_COLS = [INDEX_COL, PLAYER_COL, TEAMS_COL, SEASON_COL, GAMES_PLAYED_COL];
const PLAYER_SEASON_PLAYOFF_COLS = [INDEX_COL, PLAYER_COL, POSITION_COL, TEAMS_COL, SEASON_COL];
const PLAYER_SEASON_TEAM_ABBREVS_COLS = [
    INDEX_COL,
    PLAYER_COL,
    POSITION_COL,
    TEAM_ABBREVS,
    SEASON_COL,
    GAMES_PLAYED_COL,
];
const PLAYER_SEASON_WITHOUT_GP_COLS = [INDEX_COL, PLAYER_COL, SEASON_COL, TEAM_COL];
const PLAYER_SEASON_STREAKS_COLS = [INDEX_COL, PLAYER_COL, TEAM_COL, SEASON_COL];
const PLAYER_MULTI_SEASON_STREAKS_COLS = [INDEX_COL, PLAYER_COL, TEAM_COL];
const SERIES_COLS = [INDEX_COL, PLAYER_COL, SEASON_COL, TEAM_COL, OPPONENT_COL, SERIES_TITLE, GAMES_PLAYED_COL];
const SCF_GAME_COLS = [INDEX_COL, PLAYER_COL, POSITION_COL, TEAM_COL, SEASON_COL, GAME_COL, SERIES_GAME_NUMBER_COL];
const SCF_SKATER_GAME_COLS = [INDEX_COL, PLAYER_COL, POSITION_COL, TEAM_COL, SEASON_COL, GAME_COL, GAME_NUMBER_COL];
const SCF_GOALIE_GAME_COLS = [INDEX_COL, PLAYER_COL, TEAM_COL, SEASON_COL, GAME_COL, GAME_NUMBER_COL];
const SCF_SERIES_BASE_COLS = [INDEX_COL, PLAYER_COL, POSITION_COL, TEAM_COL, SEASON_COL, OPPONENT_COL];
const SCF_SERIES_COLS = [INDEX_COL, PLAYER_COL, POSITION_COL, TEAM_COL, SEASON_COL, OPPONENT_COL, GAMES_PLAYED_COL];
const PLAYOFF_SERIES_COLS = [INDEX_COL, PLAYER_COL, POSITION_COL, TEAM_COL, OPPONENT_COL, SEASON_COL, SERIES_TITLE];
const TEAM_ALL_TIME = [INDEX_COL, TEAM_COL, GAMES_PLAYED_COL];
const TEAM_ALL_TIME_HOME = [INDEX_COL, TEAM_COL, HOME_GAMES_PLAYED_COL];
const TEAM_ALL_TIME_ROAD = [INDEX_COL, TEAM_COL, ROAD_GAMES_PLAYED_COL];
const TEAM_GAME_COLS = [INDEX_COL, TEAM_COL, GAME_COL];
const TEAM_SEASON_COLS = [INDEX_COL, TEAM_COL, SEASON_COL, GAMES_PLAYED_COL];
const TEAM_ANY_MONTH_COLS = [INDEX_COL, TEAM_COL];
const TEAM_MONTH_COLS = [INDEX_COL, TEAM_COL, SEASON_COL];
const TEAM_SEASON_HOME_COLS = [INDEX_COL, TEAM_COL, SEASON_COL, HOME_GAMES_PLAYED_COL];
const TEAM_SEASON_ROAD_COLS = [INDEX_COL, TEAM_COL, SEASON_COL, ROAD_GAMES_PLAYED_COL];
const SERIES_GAME_COLS = [SERIES_TITLE, SERIES_GAME_NUMBER_COL];
const WIN_LOSS_TIE_COLS = [WINS_COL, LOSSES_COL, TIES_COL];
const PP_GAP_COLUMNS = [POWER_PLAY_GOALS_COL, POWER_PLAY_ASSISTS_COL, POWER_PLAY_POINTS_COL];
const SH_GAP_COLUMNS = [SHORTHAND_GOALS_COL, SHORTHAND_ASSISTS_COL, SHORTHAND_POINTS_COL];
const OT_RECORD_COLS = [
    OVERTIME_GAMES_PLAYED_COL,
    OVERTIME_WINS_COL,
    OVERTIME_LOSSES_COL,
    OVERTIME_TIES_COL,
    OVERTIME_WIN_PERCENTAGE_COL,
];
const AMATEUR_LEAGUE_AND_TEAM_COLS = [AMATEUR_LEAGUE_COL, AMATEUR_TEAM_COL];

export default {
    CreateHeaderCell,
    getBoxScoreContent,
    AGE_COLUMN,
    ASSISTS_COL,
    BLOCKED_SHOTS_COL,
    CUPS_WON_COL,
    INDEX_COL,
    CURRENT_INDEX_COL,
    FACEOFFS_LOST_COL,
    FACEOFFS_TAKEN_COL,
    FACEOFF_WIN_PCTG_COL,
    FACEOFFS_WON_COL,
    FIRST_OUTDOOR_SEASON_COL,
    LAST_OUTDOOR_SEASON_COL,
    LONGEST_STREAK_COL,
    STREAK_DESC_COL,
    GAME_COL,
    GAME_DATE_COL,
    GAMES_PLAYED_COL,
    GAMES_STARTED_COL,
    GIVEAWAYS_COL,
    GOALS_AGAINST_COL,
    GOALS_COL,
    POWER_PLAY_GOALS_COL,
    POWER_PLAY_ASSISTS_COL,
    POWER_PLAY_POINTS_COL,
    SHORTHAND_GOALS_COL,
    HITS_COL,
    BLOCKS_COL,
    LOSSES_COL,
    OVERTIME_GAMES_PLAYED_COL,
    OVERTIME_WINS_COL,
    OVERTIME_LOSSES_COL,
    OVERTIME_TIES_COL,
    OVERTIME_WIN_PERCENTAGE_COL,
    OT_PERIOD_COL,
    MISSED_SHOTS_COL,
    SHOTS_COL,
    PERIOD_COL,
    GOAL_ONE_COL,
    GOAL_TWO_COL,
    GOAL_THREE_COL,
    ASSIST_ONE_COL,
    ASSIST_TWO_COL,
    ASSIST_THREE_COL,
    SINCE_START_OF_PERIOD_COL,
    PENALTIES_COL,
    PENALTY_MINUTES_COL,
    PLAYER_COL,
    POSITION_COL,
    YOUNGEST_BASE_COLS,
    PLAYOFF_SEASONS_COL,
    POINTS_COL,
    ROUND_NUMBER_COL,
    GAME_SCORE_COL,
    SCORE_COL,
    SEASON_COL,
    SEASONS_PLAYED_COL,
    SERIES_TITLE,
    EVENT_NAME_COL,
    SHOTS_AGAINST_COL,
    SHUTOUTS_COL,
    SEASONS_WON_COL,
    TAKEAWAYS_COL,
    TEAM_COL,
    TEAM_DISPLAY_COL,
    TEAMS_COL,
    TEAM_ABBREVS,
    TIES_COL,
    TIME_ON_ICE_COL,
    TIME_ON_ICE_PER_GAME_COL,
    WIN_STREAK_COL,
    WINS_COL,
    GAP_COLUMNS,
    LONGEST_TIME_COLUMNS,
    START_END_SEASON_COLS,
    PLAYER_ALL_TIME,
    PLAYER_ALL_TIME_PLAYOFFS,
    PLAYER_ALL_TIME_WITHOUT_GP_COLS,
    PLAYER_BASE_COLS,
    PLAYER_GAME_COLS,
    PLAYER_TEAM_BASE_COLS,
    PLAYER_TEAMS_BASE_COLS,
    GOALIE_GAME_COLS,
    PLAYER_SEASON_COLS,
    PLAYER_SEASON_PLAYOFF_COLS,
    PLAYER_SEASON_WITHOUT_GP_COLS,
    PLAYER_SEASON_STREAKS_COLS,
    PLAYER_MULTI_SEASON_STREAKS_COLS,
    PLAYER_STANLEY_CUPS_COLS,
    SCORING_POINTS_COL,
    SERIES_COLS,
    PLAYOFF_SERIES_COLS,
    TEAM_ALL_TIME,
    TEAM_ALL_TIME_HOME,
    TEAM_ALL_TIME_ROAD,
    TEAM_GAME_COLS,
    TEAM_SEASON_COLS,
    TEAM_MONTH_COLS,
    TEAM_SEASON_HOME_COLS,
    TEAM_SEASON_ROAD_COLS,
    GOALS_FOR_COL,
    MULTI_GOAL_GAMES_COL,
    MULTI_ASSIST_GAMES_COL,
    MULTI_POINT_GAMES_COL,
    MULTI_GAP_GAMES_COLUMNS,
    PLAYER_SEASON_TEAM_ABBREVS_COLS,
    PLAYER_MULTI_ALL_TIME,
    SERIES_GAME_COLS,
    SERIES_GAME_NUMBER_COL,
    OPPONENT_COL,
    TEAM_ANY_MONTH_COLS,
    WIN_LOSS_TIE_COLS,
    PP_GAP_COLUMNS,
    SH_GAP_COLUMNS,
    OT_RECORD_COLS,
    SCF_GAME_COLS,
    SCF_SKATER_GAME_COLS,
    SCF_GOALIE_GAME_COLS,
    SCF_SERIES_BASE_COLS,
    SCF_SERIES_COLS,
    APPEARANCES_COL,
    STANLEY_CUPS_COL,
    CONSEC_SEASONS_COL,
    STREAK_SEASONS_COL,
    WIN_PCTG_COL,
    START_DATE_COL,
    END_DATE_COL,
    LATE_GOAL_SCORE_COL,
    SECONDS_REMAINING_COL,
    GAME_WINNING_GOAL_COL,
    AMATEUR_LEAGUE_COL,
    AMATEUR_TEAM_COL,
    AMATEUR_LEAGUE_AND_TEAM_COLS,
    HOME_GAMES_PLAYED_COL,
    ROAD_GAMES_PLAYED_COL,
};

export { addVideoToGameDateColumn, addVideoToIndexColumn };
